import { useEffect, useState } from 'react';

/**
 * Ticks a server-supplied ETA down locally between updates.
 *
 * The backend only pushes a fresh estimate when its DB state actually changes
 * (see `videoSocket.ts` — driven by Postgres NOTIFY, not a fixed cadence), so
 * without this the number sits still for a minute and then jumps. This counts
 * down once a second from the last known value and resyncs the moment a new
 * server value arrives.
 *
 * Lifted out of `JobProgressChecklist` so the sidebar's in-progress card counts
 * down the same way rather than re-deriving it.
 */
export function useEtaCountdown(etaSeconds: number | undefined): number | undefined {
  const [remaining, setRemaining] = useState(etaSeconds);

  useEffect(() => {
    setRemaining(etaSeconds);
  }, [etaSeconds]);

  useEffect(() => {
    if (etaSeconds === undefined) return;
    const interval = setInterval(() => {
      setRemaining((prev) => (prev === undefined ? prev : Math.max(0, prev - 1)));
    }, 1000);
    return () => clearInterval(interval);
  }, [etaSeconds]);

  return remaining;
}
